import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { MarkLast, ReadingPage, readingBody, readingEyebrow, readingH1, readingH2, readingLink, readingSection, readingUpdated } from "@/components/public/reading-page";
import { Faqs } from "@/components/landing/faqs";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { LegalBlock, legalHead, type LegalSection } from "@/components/public/legal-page";
import { listServices } from "@/lib/public.functions";
import { formatUsdShort } from "@/lib/payments/currency";
import { publicSiteSettingsQuery } from "@/routes/_public/route";
import { supportWhatsAppUrl } from "@/lib/social";
import { CONTACT_EMAIL } from "@/lib/legal/seller";
import { btn, tile } from "@/components/ds/styles";

export const Route = createFileRoute("/_public/help")({
  head: () =>
    legalHead(
      "/help",
      "مركز المساعدة",
      "كيف تحجز استشارة، طرق الدفع والأسعار، البوصلة والبوصلة الذكية، الحساب، الاسترداد، والتواصل مع شخص من الفريق.",
    ),
  // Plain loader data (no query cache) so server and browser render the same prices.
  loader: () => listServices().catch(() => []),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: HelpPage,
});

const ar: LegalSection[] = [
  {
    id: "booking",
    title: "كيف يتم حجز استشارة؟",
    body: [
      "1. المراسلة على واتساب أول شي للاتفاق على اليوم والساعة.",
      "2. إرسال طلب الحجز من صفحة «حجز استشارة» مع استمارة قصيرة عن الطفل.",
      "3. الدفع بالطريقة المناسبة لمكانك ورفع الإيصال.",
      "4. تأكيد الجلسة بعد مراجعة الدفع. وبالعيادة في كمان دفع كاش يوم الموعد.",
    ],
  },
  {
    id: "payment",
    title: "طرق الدفع",
    body: [
      "من خارج سوريا: PayPal.",
      "من داخل سوريا: حوالة شام كاش أو الهرم. الدفع بالبطاقة غير متاح داخل سوريا.",
      "بيانات البطاقة يتعامل معها PayPal مباشرة ولا نخزّنها أبداً، والموقع يعمل عبر HTTPS.",
    ],
  },
  {
    id: "bousala",
    title: "البوصلة المجانية",
    body: "اختبار البوصلة مجاني دائماً: يساعد الطالب يكتشف التخصّصات الأقرب له. البداية من صفحة البوصلة، وبدون دفع.",
  },
  {
    id: "smart",
    title: "البوصلة الذكية: كيف تمشي ووين التقرير؟",
    body: [
      "1. طلب البوصلة الذكية والدفع ورفع الإيصال.",
      "2. بعد مراجعة الدفع يوصلك رابط التفعيل على واتساب.",
      "3. إجراء اختبار البوصلة (أو اعتماد نتيجتك السابقة) وتعبئة نموذج التفضيلات.",
      "4. يصل التقرير خلال 24 ساعة من إتمام النموذج، وتلاقيه في حسابك على صفحة «البوصلة الذكية» (/portal/smart).",
    ],
  },
  {
    id: "account",
    title: "الحساب وتسجيل الدخول",
    body: "تسجيل الدخول بالبريد الإلكتروني أو بحساب Google. إذا ما وصلك رابط الدخول، ممكن يكون بمجلد الرسائل غير المرغوبة. وإذا ما كان هناك، التواصل معنا متاح دائماً.",
  },
  {
    id: "refund",
    title: "هل يمكن استرداد المبلغ؟",
    body: "كل المبيعات نهائية، فما في استرداد عند الإلغاء أو عدم الحضور أو تغيير الرأي، بالجلسات والباقة والبوصلة الذكية والدورات. في استثناءات قليلة من طرفنا (مثل إلغاء الجلسة منّا أو دفع مكرَّر)، وفيها نردّ خلال 48 ساعة عمل ونرجّع المبلغ بنفس طريقة الدفع خلال 10 أيام عمل؛ التفاصيل بسياسة الاسترداد والإلغاء (/refund).",
  },
  {
    id: "delete-account",
    title: "كيف أحذف حسابي؟",
    body: "طلب الحذف برسالة من البريد المسجَّل في الحساب أو على واتساب. نحذف الحساب وبياناته، ونبلغك بأي شيء يفرض القانون الاحتفاظ به (مثل سجلات الدفع).",
  },
  {
    id: "accessibility",
    title: "سهولة الاستخدام",
    body: "نحاول أن يكون الموقع سهلاً للجميع، ومنه القراءة بقارئ الشاشة والتنقّل بلوحة المفاتيح. وأي صعوبة بالاستخدام، يكفي تخبّرونا فيها ونصلحها.",
  },
];

const en: LegalSection[] = [
  { title: "How booking works", body: "Message us on WhatsApp first to agree the day and time → send the booking request with a short form about your child → pay and upload the receipt → we confirm the session after reviewing the payment. For in-clinic sessions you can also pay cash on the day." },
  { title: "Payment methods", body: "Outside Syria: PayPal (PayPal account or card). Inside Syria: Sham Cash or Al-Haram transfer — card payments are not available in Syria. Card details are handled by PayPal and never stored by us; the site runs over HTTPS. Prices are in USD." },
  { title: "Free Bousala test", body: "The Bousala test is always free and helps students find the majors that suit them best." },
  { title: "Smart Bousala", body: "Order and pay → receive your activation link on WhatsApp after the payment is reviewed → take the Bousala test and complete the preferences form → your report arrives within 24 hours of completing the form, in your account at /portal/smart." },
  { title: "Account and sign-in", body: "Sign in with email or with Google. If a sign-in email doesn't arrive, check your spam folder or contact us." },
  { title: "Can I get a refund?", body: "All sales are final: there is no refund for cancellations, missed sessions or a change of mind, across sessions, the package, Smart Bousala and courses. A few provider-side exceptions apply (for example if we cancel a session or a payment was duplicated); in those cases we reply within 48 business hours and return the amount to the same payment method within 10 business days — see the Refund, Cancellation & Delivery Policy (/refund)." },
  { title: "Deleting your account", body: "Email us from the account's address or message us on WhatsApp. We delete the account and its data and tell you what, if anything, the law requires us to keep." },
  { title: "Accessibility", body: "We aim to make the site usable for everyone, including screen-reader and keyboard users. If something doesn't work for you, tell us and we'll fix it." },
];

const tapLink = `${readingLink} inline-flex min-h-11 items-center`;

function HelpPage() {
  const services = Route.useLoaderData();
  const { data: settings } = useQuery(publicSiteSettingsQuery());
  const whatsapp = settings?.clinic_whatsapp?.trim() ? supportWhatsAppUrl(settings.clinic_whatsapp, "مرحباً، عندي سؤال") : "";

  return (
    <ReadingPage
      header={
        <>
          <p className={readingEyebrow}>مركز المساعدة</p>
          <h1 className={readingH1}><MarkLast text="كيف فينا نساعدك؟" /></h1>
          <p className={readingUpdated}>آخر تحديث: تشرين الأول 2026</p>
        </>
      }
    >
      <section>
        <h2 id="faq" className="scroll-mt-24 font-display text-[30px] font-extrabold text-text-primary">أسئلة شائعة</h2>
        <div className="mt-5">
          <Faqs items={ar.map((s) => ({ q: s.title, a: (Array.isArray(s.body) ? s.body : [s.body]).join(" ") }))} />
        </div>
      </section>

      <div className="mt-12 space-y-8">
        <LegalBlock className={readingSection} id="prices" title="الأسعار" body="كل الأسعار بالدولار الأمريكي (USD). من داخل سوريا يُدفع المبلغ المعادل بالليرة السورية حسب التعليمات المعروضة عند الدفع.">
          {services.length > 0 && (
            <ul className={tile.cream + " mt-4 divide-y divide-hairline-subtle px-5"}>
              {services.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                  <span className="font-display text-[17px] font-bold text-text-primary">{s.title_ar}</span>
                  <span dir="ltr" className="font-display text-[17px] font-extrabold text-text-primary">{formatUsdShort(s.price_usd)}</span>
                </li>
              ))}
            </ul>
          )}
          <p className={readingBody}>
            سعر البوصلة الذكية معروض على صفحة{" "}
            <Link to="/bousala/smart" className={tapLink}>طلب البوصلة الذكية</Link>.
          </p>
        </LegalBlock>

        <LegalBlock className={readingSection} id="human" title="بدّك تحكي مع شخص من الفريق؟" body={`يردّ عليك فريق د. سارة النحّاس خلال 48 ساعة عمل.`}>
          <ul className="mt-3 space-y-2 text-[17px] leading-[1.85] text-brown-500">
            <li>البريد: <a href={`mailto:${CONTACT_EMAIL}`} dir="ltr" className={tapLink}>{CONTACT_EMAIL}</a></li>
            <li>أو من <Link to="/contact" className={tapLink}>صفحة التواصل</Link>.</li>
          </ul>
          <p className="mt-3 text-[14px] leading-[1.85] text-brown-500">
            للمزيد: <Link to="/refund" className={tapLink}>سياسة الاسترداد والإلغاء</Link> ·{" "}
            <Link to="/terms" className={tapLink}>الشروط</Link> · <Link to="/privacy" className={tapLink}>الخصوصية</Link> ·{" "}
            <Link to="/disclaimer" className={tapLink}>إخلاء المسؤولية</Link>
          </p>
        </LegalBlock>
      </div>

      {whatsapp && (
        <section className="mt-12 rounded-ds-2xl bg-inverse px-6 py-10 text-cream-50 lg:px-10">
          <h2 className="font-display text-[28px] font-extrabold leading-tight">سؤال ما لقيتوا جوابه؟</h2>
          <a
            href={whatsapp}
            target="_blank"
            rel="noreferrer"
            className={btn.inversePrimary + " mt-6"}
          >
            المراسلة على واتساب ←
          </a>
        </section>
      )}

      <div dir="ltr" lang="en" className="reveal mt-16 border-t border-hairline-default pt-10 text-start">
        <h2 className="font-display text-[30px] font-extrabold text-text-primary">Help Centre</h2>
        <p className={readingUpdated}>Last updated: October 2026</p>
        <div className="mt-8 space-y-8">
          {en.map((s) => <LegalBlock key={s.title} {...s} />)}
          <LegalBlock title="Talk to a person" body={`Dr. Sara Alnahhas's team replies within 48 business hours. Email ${CONTACT_EMAIL}, message us on WhatsApp, or use the contact page.`} />
        </div>
      </div>
    </ReadingPage>
  );
}
